import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-[#111] border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <Link to="/" className="flex items-center text-amber-500 hover:text-amber-400 transition-colors">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-[#111] rounded-2xl border border-gray-800 p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400">Last updated: {currentDate}</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8 text-gray-300 leading-relaxed">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p>
                Welcome to HorseGO ("we," "our," or "us"). We are committed to protecting your privacy 
                and ensuring the security of your personal information. This Privacy Policy explains how 
                we collect, use, disclose, and safeguard your information when you use our mobile 
                application and website (collectively, the "Service").
              </p>
              <p className="mt-3">
                By using HorseGO, you agree to the collection and use of information in accordance with 
                this policy. If you do not agree with the terms of this Privacy Policy, please do not 
                access or use our Service.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">2.1 Personal Information</h3>
              <p>When you register for an account or use our Service, we may collect:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Name (first and last name)</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Postal address and city</li>
                <li>Password (stored in encrypted form)</li>
              </ul>

              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">2.2 Horse Transport Information</h3>
              <p>To provide our transport services, we collect:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Pickup and delivery locations</li>
                <li>Number of horses to transport</li>
                <li>Preferred transport dates</li>
                <li>Special requirements or notes about your horses</li>
              </ul>

              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">2.3 Transporter Information</h3>
              <p>For transporters registering on our platform:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Company name and business details</li>
                <li>Contact person information</li>
                <li>Coverage areas and service regions</li>
                <li>Fleet size and experience years</li>
                <li>Vehicle and insurance information</li>
              </ul>

              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">2.4 Automatically Collected Information</h3>
              <p>When you use our Service, we may automatically collect:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Device information (device type, operating system)</li>
                <li>IP address and general location data</li>
                <li>App usage statistics and preferences</li>
                <li>Push notification tokens (with your consent)</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li><strong>Service Delivery:</strong> To facilitate horse transport requests and connect customers with transporters</li>
                <li><strong>Account Management:</strong> To create and manage your account, authenticate users, and provide customer support</li>
                <li><strong>Communication:</strong> To send you booking confirmations, quotes, status updates, and service-related notifications</li>
                <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our Service</li>
                <li><strong>Safety & Security:</strong> To verify transporter credentials and maintain platform integrity</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Information Sharing and Disclosure</h2>
              <p>We may share your information in the following circumstances:</p>
              
              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">4.1 With Transporters/Customers</h3>
              <p>
                When you request a transport quote, relevant information (pickup/delivery locations, dates, 
                contact details) is shared with transporters to provide quotes. Similarly, when a quote is 
                accepted, customer and transporter details are shared to facilitate the transport.
              </p>

              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">4.2 Service Providers</h3>
              <p>
                We may share information with third-party service providers who assist us in operating 
                our Service (e.g., email services, cloud hosting, analytics). These providers are 
                contractually bound to protect your information.
              </p>

              <h3 className="text-xl font-medium text-amber-500 mt-6 mb-3">4.3 Legal Requirements</h3>
              <p>
                We may disclose your information if required by law, court order, or government request, 
                or to protect the rights, property, or safety of HorseGO, our users, or others.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                These measures include:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Encryption of passwords and sensitive data</li>
                <li>Secure HTTPS connections for all data transmission</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
              </ul>
              <p className="mt-3">
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention</h2>
              <p>
                We retain your personal information for as long as your account is active or as needed 
                to provide you with our Service. We may also retain certain information as required by 
                law or for legitimate business purposes, such as:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Resolving disputes and enforcing agreements</li>
                <li>Complying with legal obligations</li>
                <li>Maintaining business records for accounting purposes</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights and Choices</h2>
              <p>You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            {/* Push Notifications */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Push Notifications</h2>
              <p>
                With your consent, we may send push notifications to your mobile device to provide 
                updates about your transport requests, quotes, and booking status. You can manage 
                your notification preferences in your device settings or within the app at any time.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Services</h2>
              <p>
                Our Service may contain links to third-party websites or services (such as Google Maps 
                for location services). We are not responsible for the privacy practices of these 
                third parties. We encourage you to review their privacy policies before providing 
                any personal information.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
              <p>
                Our Service is not intended for children under 16 years of age. We do not knowingly 
                collect personal information from children under 16. If you become aware that a child 
                has provided us with personal information, please contact us, and we will take steps 
                to delete such information.
              </p>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your 
                country of residence. These countries may have different data protection laws. 
                By using our Service, you consent to the transfer of your information to these 
                countries. We ensure appropriate safeguards are in place to protect your data.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices 
                or for legal, operational, or regulatory reasons. We will notify you of any material 
                changes by posting the updated policy on this page and updating the "Last updated" date. 
                Your continued use of the Service after any changes constitutes acceptance of the 
                updated Privacy Policy.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or 
                our data practices, please contact us:
              </p>
              <div className="mt-4 p-6 bg-[#1a1a1a] rounded-xl border border-gray-700">
                <p className="font-semibold text-amber-500 text-lg mb-3">HorseGO</p>
                <p>Email: <a href="mailto:privacy@horsego.ma" className="text-amber-500 hover:underline">privacy@horsego.ma</a></p>
                <p className="mt-2">Website: <a href="/" className="text-amber-500 hover:underline">www.horsego.ma</a></p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} HorseGO. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
